import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useFormikContext, FieldArray } from 'formik';

import { ICertificateTypeListItemFragment } from 'graphql/types';
import { AddButton } from 'common/components/Button/AddButton';
import { Divider } from 'common/components/Divider';
import { ListPanel } from 'common/components/ListPanel';
import { CertificateSelectDrawer } from 'training/components/CertificateSelectDrawer';
import { getCertificateSelectStatus } from 'training/utils/certificateSelect';
import { TSkillFormCertificate } from 'skill/types';

import { SkillCertificateFieldListItem } from './SkillCertificateFieldListItem';

interface IProps {
    name: string;
    value: TSkillFormCertificate[];
}

export const SkillCertificateField = ({ name, value }: IProps) => {
    const { setFieldValue } = useFormikContext();
    const [translate] = useTranslation();
    const [drawerOpen, setDrawerOpen] = useState(false);

    const addedCertificates = value;

    const [selectedCertificates, setSelectedCertificates] =
        useState<ICertificateTypeListItemFragment[]>(addedCertificates);

    useEffect(() => {
        if (drawerOpen) {
            setSelectedCertificates(addedCertificates);
        }
    }, [drawerOpen, addedCertificates]);

    const handleSelectCertificate = (
        certificate: ICertificateTypeListItemFragment
    ) => {
        const newCertificates = [...selectedCertificates, certificate].sort(
            (a, b) => (a.name > b.name ? 1 : -1)
        );

        setSelectedCertificates(newCertificates);
    };

    const handleAddCertificates = () => {
        const newCertificates = selectedCertificates.map((certificate) => {
            const existing = value.find((c) => c.id === certificate.id);

            return {
                ...certificate,
                isMandatory: existing?.isMandatory ?? false,
            };
        });

        setFieldValue(name, newCertificates);

        setDrawerOpen(false);
    };

    const handleRemoveCertificate = (
        certificate: ICertificateTypeListItemFragment,
        removeAdded?: boolean
    ) => {
        const { isSelected } = getCertificateSelectStatus(
            selectedCertificates,
            addedCertificates,
            certificate
        );

        if (isSelected) {
            const newSelectedCertificates = selectedCertificates.filter(
                (selectedCertificate) =>
                    selectedCertificate.id !== certificate.id
            );

            setSelectedCertificates(newSelectedCertificates);
        }

        // When an added certificate is removed, it needs to be removed from the form
        if (!removeAdded) return;

        const newCertificates = addedCertificates.filter(
            (addedCertificate) => certificate.id !== addedCertificate.id
        );

        setFieldValue(name, newCertificates);
    };

    return (
        <FieldArray
            name={name}
            render={(arrayHelpers) => {
                const { form } = arrayHelpers;
                const addedCertificates =
                    (form.values[name] as TSkillFormCertificate[]) || [];
                const error = form.errors[name];
                const touched = form.touched[name];

                const addButtonHeader = (
                    <AddButton
                        aria-label={translate(
                            'skillCertificateField.addButton'
                        )}
                        error={
                            !!error && touched
                                ? (form.errors[name] as string)
                                : undefined
                        }
                        onClick={() => setDrawerOpen(true)}
                    >
                        {translate('skillCertificateField.addButton')}
                    </AddButton>
                );

                return (
                    <>
                        <ListPanel header={addButtonHeader}>
                            {!addedCertificates.length && (
                                <Typography sx={{ py: 2 }} variant="body2">
                                    {translate(
                                        'skillCertificateField.noCertificatesAdded'
                                    )}
                                </Typography>
                            )}

                            {addedCertificates.map(
                                (addedCertificate, index) => (
                                    <Box key={addedCertificate.id}>
                                        <SkillCertificateFieldListItem
                                            certificate={addedCertificate}
                                            isMandatoryFieldName={`${name}.${index}.isMandatory`}
                                            onDelete={(certificate) => {
                                                handleRemoveCertificate(
                                                    certificate,
                                                    true
                                                );
                                            }}
                                        />

                                        {index <
                                            addedCertificates.length - 1 && (
                                            <Divider />
                                        )}
                                    </Box>
                                )
                            )}
                        </ListPanel>

                        <CertificateSelectDrawer
                            addedCertificates={addedCertificates}
                            open={drawerOpen}
                            selectedCertificates={selectedCertificates}
                            setSelectedCertificates={setSelectedCertificates}
                            onAddCertificates={handleAddCertificates}
                            onClose={() => setDrawerOpen(false)}
                            onRemoveCertificate={handleRemoveCertificate}
                            onSelectCertificate={handleSelectCertificate}
                        />
                    </>
                );
            }}
        />
    );
};
